import { useEffect, useMemo, useRef, useState } from 'react';
import { PageHeader } from '../../../components/PageHeader';
import { Button } from '../../../components/Button';
import { Input } from '../../../components/Input';
import { Textarea } from '../../../components/Textarea';
import { StatCard } from '../../../components/StatCard';
import { Avatar, AvatarImage, AvatarFallback } from '../../../components/Avatar';
import { EmptyState } from '../../../components/EmptyState';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../../components/Dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/Select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../../components/Tabs';
import { useEngagementStore, RosterRow, FestivalRow, Award as AwardItem } from '../../../store/engagementStore';
import { useRbacStore } from '../../../store/rbacStore';
import { getUserAvatarUrl } from '../../../utils/avatar';
import { WishKind, KIND_META, toWishItems, WishCard, DEFAULT_WISH_BODIES } from './wishKit';
import { PartyPopper, Search, Upload, Trash2, Plus, Award, Cake, Settings2, CalendarHeart, FileText } from 'lucide-react';
import toast from 'react-hot-toast';

/** Read a file and downscale it to a small square-ish JPEG data URL for DB storage. */
async function fileToDataUrl(file: File, max = 400): Promise<string> {
  const raw = await new Promise<string>((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result as string);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = reject;
    i.src = raw;
  });
  const scale = Math.min(1, max / Math.max(img.width, img.height));
  const w = Math.round(img.width * scale);
  const h = Math.round(img.height * scale);
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  canvas.getContext('2d')!.drawImage(img, 0, 0, w, h);
  return canvas.toDataURL('image/jpeg', 0.82);
}

const initials = (name: string) =>
  name.split(' ').map((p) => p[0]).filter(Boolean).slice(0, 2).join('').toUpperCase() || '?';

function PhotoAvatar({ name, email, photoUrl, size = 'md' as const }: { name: string; email?: string; photoUrl?: string | null; size?: 'sm' | 'md' | 'lg' }) {
  return (
    <Avatar size={size}>
      <AvatarImage src={photoUrl || getUserAvatarUrl(name, email)} alt={name} />
      <AvatarFallback>{initials(name)}</AvatarFallback>
    </Avatar>
  );
}

// =======================================================================
//  Celebrations feed (everyone) — 5 summary cards + wish cards
// =======================================================================

function CelebrationsFeed() {
  const { today, fetchToday } = useEngagementStore();
  const [filter, setFilter] = useState<WishKind | null>(null);

  useEffect(() => { fetchToday(); }, [fetchToday]);

  const items = useMemo(() => toWishItems(today), [today]);

  const counts = useMemo(() => ({
    birthday: today?.birthdays?.length || 0,
    anniversary: today?.anniversaries?.length || 0,
    festival: today?.festivals?.length || 0,
    award: today?.awards?.length || 0,
    new_joiner: today?.newJoiners?.length || 0,
  }), [today]);

  const cards: { kind: WishKind; label: string }[] = [
    { kind: 'birthday', label: 'Birthdays' },
    { kind: 'anniversary', label: 'Work Anniversaries' },
    { kind: 'festival', label: 'Festivals' },
    { kind: 'award', label: 'Awards & Spotlight' },
    { kind: 'new_joiner', label: 'New Joiners' },
  ];

  const shown = filter ? items.filter((i) => i.kind === filter) : items;

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {cards.map((c) => {
          const Icon = KIND_META[c.kind].icon;
          return (
            <StatCard
              key={c.kind}
              label={c.label}
              value={counts[c.kind]}
              icon={<Icon />}
              hint="today"
              className={filter === c.kind ? 'ring-2 ring-primary' : ''}
              onClick={() => setFilter((f) => (f === c.kind ? null : c.kind))}
            />
          );
        })}
      </div>

      {shown.length === 0 ? (
        <EmptyState
          icon={<PartyPopper />}
          title="No celebrations today"
          description="Birthdays, work anniversaries, new joiners, festivals and awards will appear here on the day."
        />
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {shown.map((item) => <WishCard key={item.key} item={item} />)}
        </div>
      )}
    </div>
  );
}

// =======================================================================
//  Admin: Employee (DOB + anniversary + photo) editor
// =======================================================================

function EmployeeDialog({ row, onClose }: { row: RosterRow; onClose: () => void }) {
  const { updateEmployee } = useEngagementStore();
  const [dob, setDob] = useState(row.dateOfBirth ? String(row.dateOfBirth).slice(0, 10) : '');
  const [join, setJoin] = useState(row.joinDate ? String(row.joinDate).slice(0, 10) : '');
  const [photo, setPhoto] = useState<string | null>(row.photoUrl ?? null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const onPick = async (file?: File) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) return toast.error('Please choose an image file');
    try { setPhoto(await fileToDataUrl(file)); } catch { toast.error('Could not read that image'); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await updateEmployee(row.id, { dateOfBirth: dob || null, joinDate: join || null, photoUrl: photo });
      toast.success('Saved');
      onClose();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{row.name}</DialogTitle>
          <DialogDescription>Set the birthday, work anniversary and photo used across celebration wishes.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <PhotoAvatar name={row.name} email={row.email} photoUrl={photo} size="lg" />
            <div className="flex items-center gap-2">
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => onPick(e.target.files?.[0])} />
              <Button variant="secondary" size="sm" onClick={() => fileRef.current?.click()}>
                <Upload className="size-4" /> Upload photo
              </Button>
              {photo && <Button variant="ghost" size="sm" onClick={() => setPhoto(null)}>Remove</Button>}
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">Date of birth</label>
              <Input type="date" value={dob} onChange={(e) => setDob(e.target.value)} />
              <p className="text-xs text-muted-foreground">Day and month only — the year is never shown.</p>
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">Work anniversary (joining date)</label>
              <Input type="date" value={join} onChange={(e) => setJoin(e.target.value)} />
              <p className="text-xs text-muted-foreground">Celebrated on this date each year.</p>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EmployeesTab() {
  const { roster, fetchRoster, isLoading } = useEngagementStore();
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<RosterRow | null>(null);

  useEffect(() => { fetchRoster(); }, [fetchRoster]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return roster;
    return roster.filter((r) => `${r.name} ${r.email} ${r.employeeCode}`.toLowerCase().includes(q));
  }, [roster, search]);

  return (
    <div className="space-y-4">
      <div className="relative max-w-sm">
        <Search className="absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input className="pl-8" placeholder="Search employees…" value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>

      <div className="overflow-hidden rounded-lg border border-border bg-card">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-secondary/40 text-left text-xs text-muted-foreground">
              <th className="px-4 py-2.5 font-medium">Employee</th>
              <th className="px-4 py-2.5 font-medium">Department</th>
              <th className="px-4 py-2.5 font-medium">Birthday</th>
              <th className="px-4 py-2.5 font-medium">Work anniversary</th>
              <th className="px-4 py-2.5 font-medium">Photo</th>
              <th className="px-4 py-2.5 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r.id} className="border-b border-border last:border-0 hover:bg-secondary/30">
                <td className="px-4 py-2.5">
                  <div className="flex items-center gap-2.5">
                    <PhotoAvatar name={r.name} email={r.email} photoUrl={r.photoUrl} size="sm" />
                    <div className="min-w-0">
                      <div className="truncate font-medium text-foreground">{r.name}</div>
                      <div className="truncate text-xs text-muted-foreground">{r.email}</div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-2.5 text-muted-foreground">{r.department || '—'}</td>
                <td className="px-4 py-2.5">
                  {r.dateOfBirth
                    ? new Date(r.dateOfBirth).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
                    : <span className="text-muted-foreground">Not set</span>}
                </td>
                <td className="px-4 py-2.5">
                  {r.joinDate
                    ? new Date(r.joinDate).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
                    : <span className="text-muted-foreground">Not set</span>}
                </td>
                <td className="px-4 py-2.5">
                  {r.photoUrl ? <span className="text-success-strong">Uploaded</span> : <span className="text-muted-foreground">—</span>}
                </td>
                <td className="px-4 py-2.5 text-right">
                  <Button variant="ghost" size="sm" onClick={() => setEditing(r)}>Edit</Button>
                </td>
              </tr>
            ))}
            {!isLoading && filtered.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">No employees found.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && <EmployeeDialog row={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

// =======================================================================
//  Admin: Awards
// =======================================================================

function AwardDialog({ onClose, award }: { onClose: () => void; award?: AwardItem }) {
  const { roster, fetchRoster, createAward, updateAward } = useEngagementStore();
  const editing = !!award;
  const [employeeId, setEmployeeId] = useState<string>(award?.employeeId ?? '');
  const [title, setTitle] = useState(award?.title ?? '');
  const [description, setDescription] = useState(award?.description ?? '');
  const [awardDate, setAwardDate] = useState(award?.awardDate ? String(award.awardDate).slice(0, 10) : new Date().toISOString().slice(0, 10));
  const [photo, setPhoto] = useState<string | null>(award?.photoUrl ?? null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => { if (roster.length === 0) fetchRoster(); }, [roster.length, fetchRoster]);

  const onPick = async (file?: File) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) return toast.error('Please choose an image file');
    try { setPhoto(await fileToDataUrl(file)); } catch { toast.error('Could not read that image'); }
  };

  const save = async () => {
    if (!title.trim()) return toast.error('Give the award a title');
    setSaving(true);
    try {
      if (editing) {
        await updateAward(award!.id, { employeeId: employeeId || null, title: title.trim(), description, awardDate, photoUrl: photo });
        toast.success('Award updated');
      } else {
        await createAward({ employeeId: employeeId || null, title: title.trim(), description, awardDate, photoUrl: photo });
        toast.success('Award posted');
      }
      onClose();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit spotlight / award' : 'New spotlight / award'}</DialogTitle>
          <DialogDescription>Recognise an employee. It appears on everyone's Engagement page and dashboard on the award date.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">Employee</label>
            <Select value={employeeId} onValueChange={setEmployeeId}>
              <SelectTrigger><SelectValue placeholder="Select employee (optional)" /></SelectTrigger>
              <SelectContent>
                {roster.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">Leave empty for a team or company-wide recognition.</p>
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">Title</label>
            <Input placeholder="Employee of the Month" value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">Message</label>
            <Textarea placeholder="For outstanding contribution…" value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">Image</label>
            <div className="flex items-center gap-3">
              {photo
                ? <img src={photo} alt="award" className="h-20 w-auto max-w-[55%] rounded-lg border border-border object-contain" />
                : <div className="flex h-20 w-28 items-center justify-center rounded-lg border border-dashed border-border text-muted-foreground"><Award className="size-6" /></div>}
              <div className="flex flex-col gap-2">
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => onPick(e.target.files?.[0])} />
                <Button variant="secondary" size="sm" onClick={() => fileRef.current?.click()}>
                  <Upload className="size-4" /> {photo ? 'Change image' : 'Upload image'}
                </Button>
                {photo && <Button variant="ghost" size="sm" onClick={() => setPhoto(null)}>Remove</Button>}
              </div>
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">Award date</label>
            <Input type="date" value={awardDate} onChange={(e) => setAwardDate(e.target.value)} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving}>{saving ? 'Saving…' : (editing ? 'Save' : 'Post')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AwardsTab() {
  const { awards, fetchAwards, deleteAward } = useEngagementStore();
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<AwardItem | null>(null);

  useEffect(() => { fetchAwards(); }, [fetchAwards]);

  const remove = async (id: string) => {
    try { await deleteAward(id); toast.success('Removed'); } catch { toast.error('Could not remove'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={() => setAdding(true)}><Plus className="size-4" /> New spotlight</Button>
      </div>

      {awards.length === 0 ? (
        <EmptyState icon={<Award />} title="No awards yet" description="Recognise an employee — it will appear on everyone's Engagement page on the award date." />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {awards.map((a) => (
            <div key={a.id} className="overflow-hidden rounded-lg border border-border bg-card">
              {a.photoUrl && (
                <div className="flex items-center justify-center border-b border-border bg-secondary/30 p-3">
                  <img src={a.photoUrl} alt={a.title} className="max-h-32 w-auto max-w-full rounded object-contain" />
                </div>
              )}
              <div className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2 text-warning-strong">
                    <Award className="size-4" />
                    <span className="font-display text-card-title font-semibold text-foreground">{a.title}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" onClick={() => setEditing(a)}>Edit</Button>
                    <Button variant="ghost" size="icon-sm" onClick={() => remove(a.id)} aria-label="Remove award">
                      <Trash2 className="size-4 text-destructive" />
                    </Button>
                  </div>
                </div>
                {a.employeeName && <div className="mt-1 text-sm text-foreground">{a.employeeName}</div>}
                {a.description && <p className="mt-1 text-sm text-muted-foreground line-clamp-3">{a.description}</p>}
                <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
                  <span>{new Date(a.awardDate).toLocaleDateString()}</span>
                  {!a.photoUrl && <span className="text-warning-strong">· No image — click Edit to add</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {adding && <AwardDialog onClose={() => setAdding(false)} />}
      {editing && <AwardDialog award={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

// =======================================================================
//  Admin: Festivals (banner + custom wish on holiday-calendar festivals)
// =======================================================================

function FestivalDialog({ row, onClose }: { row: FestivalRow; onClose: () => void }) {
  const { updateFestival } = useEngagementStore();
  const [banner, setBanner] = useState<string | null>(row.bannerUrl ?? null);
  const [wish, setWish] = useState(row.wishText ?? '');
  const [saving, setSaving] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const onPick = async (file?: File) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) return toast.error('Please choose an image file');
    try { setBanner(await fileToDataUrl(file, 900)); } catch { toast.error('Could not read that image'); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await updateFestival(row.id, { bannerUrl: banner, wishText: wish.trim() || null });
      toast.success('Saved');
      onClose();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{row.name}</DialogTitle>
          <DialogDescription>Upload a banner and set a wish for {new Date(row.date).toLocaleDateString()}. Shown on everyone's dashboard on the day.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">Banner</label>
            <div className="flex items-center gap-3">
              {banner
                ? <img src={banner} alt={row.name} className="h-24 w-auto max-w-[60%] rounded-lg border border-border object-contain" />
                : <div className="flex h-24 w-40 items-center justify-center rounded-lg border border-dashed border-border text-muted-foreground"><CalendarHeart className="size-6" /></div>}
              <div className="flex flex-col gap-2">
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => onPick(e.target.files?.[0])} />
                <Button variant="secondary" size="sm" onClick={() => fileRef.current?.click()}>
                  <Upload className="size-4" /> {banner ? 'Change banner' : 'Upload banner'}
                </Button>
                {banner && <Button variant="ghost" size="sm" onClick={() => setBanner(null)}>Remove</Button>}
              </div>
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">Custom wish (optional)</label>
            <Textarea
              placeholder={`Wishing you and your loved ones a very happy ${row.name}!`}
              value={wish}
              onChange={(e) => setWish(e.target.value)}
              rows={3}
            />
            <p className="text-xs text-muted-foreground">Leave empty to use the default festival wish. One line per paragraph.</p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NewFestivalDialog({ onClose }: { onClose: () => void }) {
  const { createFestival } = useEngagementStore();
  const [name, setName] = useState('');
  const [date, setDate] = useState('');
  const [banner, setBanner] = useState<string | null>(null);
  const [wish, setWish] = useState('');
  const [saving, setSaving] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const onPick = async (file?: File) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) return toast.error('Please choose an image file');
    try { setBanner(await fileToDataUrl(file, 900)); } catch { toast.error('Could not read that image'); }
  };

  const save = async () => {
    if (!name.trim()) return toast.error('Enter the festival name');
    if (!date) return toast.error('Pick the festival date');
    setSaving(true);
    try {
      await createFestival({ name: name.trim(), date, bannerUrl: banner, wishText: wish.trim() || null });
      toast.success('Festival added');
      onClose();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New festival</DialogTitle>
          <DialogDescription>Add a festival with its date, banner and wish. It's shared on everyone's dashboard on that day.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">Festival name</label>
              <Input placeholder="Diwali" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">Date</label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </div>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">Banner</label>
            <div className="flex items-center gap-3">
              {banner
                ? <img src={banner} alt="banner" className="h-24 w-auto max-w-[60%] rounded-lg border border-border object-contain" />
                : <div className="flex h-24 w-40 items-center justify-center rounded-lg border border-dashed border-border text-muted-foreground"><CalendarHeart className="size-6" /></div>}
              <div className="flex flex-col gap-2">
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => onPick(e.target.files?.[0])} />
                <Button variant="secondary" size="sm" onClick={() => fileRef.current?.click()}><Upload className="size-4" /> {banner ? 'Change banner' : 'Upload banner'}</Button>
                {banner && <Button variant="ghost" size="sm" onClick={() => setBanner(null)}>Remove</Button>}
              </div>
            </div>
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">Wish (optional)</label>
            <Textarea placeholder="Wishing you a bright and joyful celebration!" value={wish} onChange={(e) => setWish(e.target.value)} rows={3} />
            <p className="text-xs text-muted-foreground">Leave empty to use the default festival wish.</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Add festival'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FestivalsTab() {
  const { festivals, fetchFestivals, deleteFestival } = useEngagementStore();
  const [editing, setEditing] = useState<FestivalRow | null>(null);
  const [adding, setAdding] = useState(false);

  useEffect(() => { fetchFestivals(); }, [fetchFestivals]);

  const remove = async (id: string) => {
    try { await deleteFestival(id); toast.success('Removed'); } catch { toast.error('Could not remove'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="max-w-xl text-sm text-muted-foreground">
          Add a festival with its date, banner and wish — it's shared on everyone's dashboard on that day. Festivals also show in the <span className="font-medium text-foreground">Holidays</span> calendar.
        </p>
        <Button onClick={() => setAdding(true)}><Plus className="size-4" /> New festival</Button>
      </div>

      {festivals.length === 0 ? (
        <EmptyState icon={<CalendarHeart />} title="No festivals yet" description="Add one with the New festival button above (or add holidays in People → Holidays)." />
      ) : (
        <div className="overflow-hidden rounded-lg border border-border bg-card">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-secondary/40 text-left text-xs text-muted-foreground">
                <th className="px-4 py-2.5 font-medium">Date</th>
                <th className="px-4 py-2.5 font-medium">Festival</th>
                <th className="px-4 py-2.5 font-medium">Banner</th>
                <th className="px-4 py-2.5 font-medium">Wish</th>
                <th className="px-4 py-2.5 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {festivals.map((f) => (
                <tr key={f.id} className="border-b border-border last:border-0 hover:bg-secondary/30">
                  <td className="px-4 py-2.5 text-muted-foreground">{new Date(f.date).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}</td>
                  <td className="px-4 py-2.5 font-medium text-foreground">{f.name}</td>
                  <td className="px-4 py-2.5">
                    {f.bannerUrl
                      ? <img src={f.bannerUrl} alt={f.name} className="h-8 w-auto rounded border border-border object-contain" />
                      : <span className="text-muted-foreground">—</span>}
                  </td>
                  <td className="px-4 py-2.5">{f.wishText ? <span className="text-success-strong">Set</span> : <span className="text-muted-foreground">Default</span>}</td>
                  <td className="px-4 py-2.5 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="sm" onClick={() => setEditing(f)}>Edit</Button>
                      <Button variant="ghost" size="icon-sm" onClick={() => remove(f.id)} aria-label="Remove festival"><Trash2 className="size-4 text-destructive" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {adding && <NewFestivalDialog onClose={() => setAdding(false)} />}
      {editing && <FestivalDialog row={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

// =======================================================================
//  Admin: Wish text templates
// =======================================================================

const TEMPLATE_OCCASIONS: { kind: WishKind; label: string; placeholders: string }[] = [
  { kind: 'birthday', label: 'Birthday', placeholders: '{first_name} {name}' },
  { kind: 'anniversary', label: 'Work anniversary', placeholders: '{first_name} {name} {years}' },
  { kind: 'new_joiner', label: 'New joiner', placeholders: '{first_name} {name}' },
  { kind: 'festival', label: 'Festival', placeholders: '{festival}' },
  { kind: 'award', label: 'Award / spotlight', placeholders: '{name} {award}' },
];

function TemplateEditor({ kind, label, placeholders }: { kind: WishKind; label: string; placeholders: string }) {
  const { templates, updateTemplate } = useEngagementStore();
  const [text, setText] = useState(templates[kind] ?? DEFAULT_WISH_BODIES[kind]);
  const [saving, setSaving] = useState(false);
  const isCustom = !!templates[kind];

  useEffect(() => { setText(templates[kind] ?? DEFAULT_WISH_BODIES[kind]); }, [templates, kind]);

  const save = async () => {
    setSaving(true);
    try { await updateTemplate(kind, text); toast.success('Saved'); }
    catch (e: any) { toast.error(e?.response?.data?.message || 'Could not save'); }
    finally { setSaving(false); }
  };
  const reset = async () => {
    setSaving(true);
    try { await updateTemplate(kind, null); setText(DEFAULT_WISH_BODIES[kind]); toast.success('Reset to default'); }
    catch { toast.error('Could not reset'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-2 rounded-lg border border-border bg-card p-4">
      <div className="flex items-center justify-between">
        <span className="font-display text-card-title font-semibold text-foreground">{label}</span>
        <span className={`text-xs ${isCustom ? 'text-success-strong' : 'text-muted-foreground'}`}>{isCustom ? 'Custom' : 'Default'}</span>
      </div>
      <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} />
      <p className="text-xs text-muted-foreground">
        Placeholders: <span className="font-mono text-foreground">{placeholders}</span> · one line per paragraph. The greeting (“Dear …,”) and “Warm regards, Trusty Bytes HR Team” are added automatically.
      </p>
      <div className="flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={reset} disabled={saving || !isCustom}>Reset to default</Button>
        <Button size="sm" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
      </div>
    </div>
  );
}

function TemplatesTab() {
  const { fetchTemplates } = useEngagementStore();
  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);
  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">Edit the wish message shown on the dashboard and Engagement page for each occasion. Changes apply to the on-screen wish.</p>
      {TEMPLATE_OCCASIONS.map((o) => <TemplateEditor key={o.kind} {...o} />)}
    </div>
  );
}

function ManageTab() {
  return (
    <div className="space-y-6">
      <Tabs defaultValue="employees">
        <TabsList variant="pill">
          <TabsTrigger value="employees" variant="pill"><Cake className="size-4" /> Birthdays & photos</TabsTrigger>
          <TabsTrigger value="awards" variant="pill"><Award className="size-4" /> Awards</TabsTrigger>
          <TabsTrigger value="festivals" variant="pill"><CalendarHeart className="size-4" /> Festivals</TabsTrigger>
          <TabsTrigger value="templates" variant="pill"><FileText className="size-4" /> Wish text</TabsTrigger>
        </TabsList>
        <TabsContent value="employees"><EmployeesTab /></TabsContent>
        <TabsContent value="awards"><AwardsTab /></TabsContent>
        <TabsContent value="festivals"><FestivalsTab /></TabsContent>
        <TabsContent value="templates"><TemplatesTab /></TabsContent>
      </Tabs>
    </div>
  );
}

// =======================================================================
//  Page
// =======================================================================

export function EngagementAdmin() {
  const { hasFullAccess, hasPermission } = useRbacStore();
  const canManage = hasFullAccess() || hasPermission('People', 'Settings', 'Edit') || hasPermission('People', 'Settings', 'View');

  return (
    <div className="flex-1 overflow-auto p-4 sm:p-6 space-y-4">
      <PageHeader
        icon={PartyPopper}
        title="Engagement"
        description="Celebrate birthdays, work anniversaries, new joiners, festivals and awards. React and leave a wish for your colleagues."
      />

      {canManage ? (
        <Tabs defaultValue="celebrations">
          <TabsList>
            <TabsTrigger value="celebrations"><PartyPopper className="size-4" /> Celebrations</TabsTrigger>
            <TabsTrigger value="manage"><Settings2 className="size-4" /> Manage</TabsTrigger>
          </TabsList>
          <TabsContent value="celebrations"><CelebrationsFeed /></TabsContent>
          <TabsContent value="manage"><ManageTab /></TabsContent>
        </Tabs>
      ) : (
        <CelebrationsFeed />
      )}
    </div>
  );
}
